import clsx from "classnames";
import { useState } from "react";
import {
  CONTROLLER_PAIRING_STEPS,
  OTHER_CONTROLLERS_FAQ_URL,
} from "../../../constants/controllerInstruction";
import { is720pResolution } from "../../../constants/dimension";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { CONSOLE_VIDEO_ID } from "../../../constants/videos";
import { useGuideVideo } from "../../../hooks/useGuideVideo";
import { YtbVideoPlayer } from "../YtbVideoPlayer/YtbVideoPlayer";
import { VideoPopupLayout } from "../VideoPopupLayout";
import { Dialog } from "../../core/Dialog/Dialog";
import { Typography } from "../../core/Typography/Typography";
import { Button } from "../../core/Button/Button";
import ControllerQRCodeDialog from "../ControllerQRCodeDialog/ControllerQRCodeDialog";
import { useStyles } from "./DesktopLayout.style";
import StepImage from "./StepImage";
import { useAppSelector } from "../../hooks";
import { selectIsDesktop } from "../../../store/slices/appState";

type TDesktopLayout = {
  open: boolean;
  onClose?: () => void;
  isTV?: boolean;
  videoError: boolean;
  setVideoError: (error: boolean) => void;
};

const DesktopLayout: React.FC<TDesktopLayout> = (props) => {
  const { open, onClose, isTV = false, videoError, setVideoError } = props;
  const classes = useStyles();
  const isDesktop = useAppSelector(selectIsDesktop);
  const consoleVideoUrl = useGuideVideo(CONSOLE_VIDEO_ID);
  const [showControllerQRCode, setShowControllerQRCode] = useState(false);

  const handleOtherControllersClick = () => {
    if (isTV) {
      setShowControllerQRCode(true);
      onClose?.();
    } else {
      window.open(OTHER_CONTROLLERS_FAQ_URL, "_blank", "noopener,noreferrer");
    }
  };

  // Layout: TV (QR always shown) or Desktop (no QR, opens new tab)
  if (isTV || isDesktop) {
    return (
      <>
        <VideoPopupLayout
          open={open}
          onClose={onClose}
          videoUrl={videoError ? undefined : consoleVideoUrl}
          videoTitle="Controller Connection Guide"
          onVideoError={() => setVideoError(true)}
          videoFallback={
            <div className={classes.stepContainerDesktop}>
              {CONTROLLER_PAIRING_STEPS.map((step, i) => (
                <div key={i} className={classes.stepColDesktop}>
                  <StepImage size={160} src={step.image} />
                  <Typography
                    variant={TypographyVariant.BodyB3Bold}
                    color={TypographyColor.Default}
                    className={clsx(classes.stepTitle, {
                      is720Resolution: is720pResolution,
                    })}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant={TypographyVariant.BodyB3Medium}
                    color={TypographyColor.Gray}
                    className={clsx(classes.stepDesc, {
                      is720Resolution: is720pResolution,
                    })}
                  >
                    {step.desc}
                  </Typography>
                </div>
              ))}
            </div>
          }
          qr={
            isTV
              ? { value: OTHER_CONTROLLERS_FAQ_URL, show: true, size: 80 }
              : undefined
          }
          title="Connect ONMO+ Pro Controller"
          description={
            <Typography
              variant={TypographyVariant.BodyB3Medium}
              className={classes.descLarge}
            >
              {isTV
                ? "Scan the QR code to learn more about how to pair ONMO+ Pro Controller."
                : "Learn how to pair your ONMO+ Pro Controller or view the Connection Guide for other controllers and more."}
            </Typography>
          }
          actions={
            isTV ? (
              <Button
                variant="outlined"
                color="secondary"
                onClick={onClose}
                id="close-dialog"
                data-button-sound="back"
                data-gamepad="default-selected"
              >
                Close
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  id="close-dialog"
                  data-button-sound="back"
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleOtherControllersClick}
                  id="pair-controller-other"
                  data-gamepad="default-selected"
                >
                  Connection Guide
                </Button>
              </>
            )
          }
        />
        <ControllerQRCodeDialog
          open={showControllerQRCode}
          onClose={() => setShowControllerQRCode(false)}
        />
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        className={classes.desktopContent}
        secondAction={{
          text: "Close",
          onClick: onClose,
          id: "close-dialog",
          "data-button-sound": "back",
          size: "large",
        }}
        mainAction={{
          text: "Connection Guide",
          onClick: handleOtherControllersClick,
          id: "pair-controller-other",
          size: "large",
          "data-gamepad": "default-selected",
        }}
        style={{ maxWidth: "62.5vw" }}
      >
        <Typography variant={TypographyVariant.HeadingH3Bold}>
          Connect ONMO+ Pro Controller
        </Typography>

        {!videoError && consoleVideoUrl && (
          <div className={classes.videoWrapper}>
            <YtbVideoPlayer
              url={consoleVideoUrl}
              loop
              title="Controller Connection Guide"
              className={classes.videoIframe}
              onError={() => setVideoError(true)}
            />
          </div>
        )}

        {videoError && (
          <div className={classes.stepContainerDesktop}>
            {CONTROLLER_PAIRING_STEPS.map((step, i) => (
              <div key={i} className={classes.stepColDesktop}>
                <StepImage size={160} src={step.image} />
                <Typography
                  variant={TypographyVariant.BodyB3Bold}
                  color={TypographyColor.Default}
                  className={clsx(classes.stepTitle, {
                    is720Resolution: is720pResolution,
                  })}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant={TypographyVariant.BodyB3Medium}
                  color={TypographyColor.Gray}
                  className={clsx(classes.stepDesc, {
                    is720Resolution: is720pResolution,
                  })}
                >
                  {step.desc}
                </Typography>
              </div>
            ))}
          </div>
        )}
      </Dialog>

      {isTV && (
        <ControllerQRCodeDialog
          open={showControllerQRCode}
          onClose={() => setShowControllerQRCode(false)}
        />
      )}
    </>
  );
};

export default DesktopLayout;
